import { useCallback, useEffect, useMemo, useState } from "react";
import {
  SPEAKER_NOTE_KINDS,
  removeSpeakerNote,
  sanitizeSpeakerNoteMarkdown,
  setSpeakerNoteKind,
  SPEAKER_NOTE_BODY_MAX_CHARS,
  SPEAKER_NOTE_SUMMARY_CHARS_MAX,
  SPEAKER_NOTE_SUMMARY_CHARS_MIN,
  toggleSpeakerNoteCollapsed,
  toggleSpeakerNotePin,
  truncateSpeakerNoteChars,
  updateSpeakerNote,
  type SurfDeck,
  type SurfSpeakerNote,
  type SurfSpeakerNoteKind,
} from "@vida/surf-core";
import { Menu } from "../../ui/Menu";
import { createSurfApiConfig, ensureSurfClientConfig } from "../../services/surfClientConfig";
import { summarizeSpeakerNoteCoherent } from "../../services/speakerNoteSummary";
import { SpeakerNoteRawModal } from "./SpeakerNoteRawModal";
import { SpeakerScriptView } from "./SpeakerScriptEditor";

const KIND_LABELS: Record<SurfSpeakerNoteKind, string> = {
  deepdive: "Approfondimento",
  data: "Dati",
  objection: "Obiezione",
  anecdote: "Aneddoto",
  qa: "Q&A",
  definition: "Definizione",
  freeform: "Libero",
};

function noteQuestion(note: SurfSpeakerNote): string | undefined {
  return note.question?.trim() || note.source?.question?.trim() || undefined;
}

type Props = {
  deck: SurfDeck;
  slideId: string;
  note: SurfSpeakerNote;
  index: number;
  readOnly?: boolean;
  onDeckChange: (deck: SurfDeck) => void;
  onDragStart: (index: number) => void;
  onDragOver: (index: number) => void;
  onDrop: (index: number) => void;
  onActivity?: (label: string | null) => void;
  dragOver?: boolean;
  /** Start with question/title only — body hidden until expanded. */
  defaultMinimized?: boolean;
  /** When this value changes, collapse the card again (e.g. active slide index). */
  resetCollapseKey?: string | number;
};

export function SpeakerNoteCard({
  deck,
  slideId,
  note,
  index,
  readOnly = false,
  onDeckChange,
  onDragStart,
  onDragOver,
  onDrop,
  onActivity,
  dragOver,
  defaultMinimized = false,
  resetCollapseKey,
}: Props) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(note.title);
  const [editingBody, setEditingBody] = useState(false);
  const [bodyDraft, setBodyDraft] = useState(note.body);
  const [busy, setBusy] = useState(false);
  const [rawOpen, setRawOpen] = useState(false);
  const [cardMinimized, setCardMinimized] = useState(
    () => defaultMinimized || (readOnly && resetCollapseKey !== undefined),
  );
  const [bodyExpandedLocal, setBodyExpandedLocal] = useState(false);
  const [summaryTargetOpen, setSummaryTargetOpen] = useState(false);
  const [summaryTarget, setSummaryTarget] = useState(() =>
    Math.min(
      SPEAKER_NOTE_SUMMARY_CHARS_MAX,
      Math.max(SPEAKER_NOTE_SUMMARY_CHARS_MIN, SPEAKER_NOTE_BODY_MAX_CHARS),
    ),
  );

  const question = noteQuestion(note);
  const hasRawBody = Boolean(note.rawBody?.trim() && note.rawBody.trim() !== note.body.trim());
  const isBodyExpanded = hasRawBody && (readOnly ? bodyExpandedLocal : note.collapsed === false);
  const displayMarkdown = isBodyExpanded
    ? (note.rawBody ?? note.body)
    : note.summaryStatus === "coherent"
      ? note.body
      : truncateSpeakerNoteChars(note.body);

  useEffect(() => {
    setTitleDraft(note.title);
    setBodyDraft(note.body);
    setSummaryTarget(
      Math.min(
        SPEAKER_NOTE_SUMMARY_CHARS_MAX,
        Math.max(
          SPEAKER_NOTE_SUMMARY_CHARS_MIN,
          note.body.length || SPEAKER_NOTE_BODY_MAX_CHARS,
        ),
      ),
    );
  }, [note.id, note.title, note.body]);

  useEffect(() => {
    if (resetCollapseKey === undefined) return;
    setCardMinimized(true);
    setBodyExpandedLocal(false);
  }, [resetCollapseKey, note.id]);

  const commitTitle = () => {
    setEditingTitle(false);
    const next = titleDraft.trim();
    if (!next || next === note.title) return;
    onDeckChange(updateSpeakerNote(deck, slideId, note.id, { title: next }));
  };

  const commitBody = () => {
    setEditingBody(false);
    const next = truncateSpeakerNoteChars(sanitizeSpeakerNoteMarkdown(bodyDraft));
    if (next === note.body) return;
    onDeckChange(updateSpeakerNote(deck, slideId, note.id, { body: next }));
  };

  const handleSanitize = () => {
    const target = isBodyExpanded ? (note.rawBody ?? note.body) : note.body;
    const next = sanitizeSpeakerNoteMarkdown(target);
    if (next === target) return;
    if (isBodyExpanded) {
      onDeckChange(updateSpeakerNote(deck, slideId, note.id, { rawBody: next }));
    } else {
      onDeckChange(
        updateSpeakerNote(deck, slideId, note.id, {
          body: truncateSpeakerNoteChars(next),
        }),
      );
    }
  };

  const handleToggleBodyExpand = () => {
    if (hasRawBody) {
      onDeckChange(toggleSpeakerNoteCollapsed(deck, slideId, note.id));
    }
  };

  const handleRegenerateSummary = useCallback(
    async (targetChars: number) => {
      const q = noteQuestion(note);
      const answer = note.rawBody?.trim() || note.body;
      if (!answer.trim()) return;

      setBusy(true);
      setSummaryTargetOpen(false);
      onActivity?.("Rigenera sunto…");
      try {
        await ensureSurfClientConfig();
        const { summary, status } = await summarizeSpeakerNoteCoherent(
          createSurfApiConfig(),
          q ?? "",
          answer,
          { targetChars },
        );
        if (!summary.trim()) return;
        onDeckChange(
          updateSpeakerNote(deck, slideId, note.id, {
            body: summary.trim(),
            summaryStatus: status,
            collapsed: true,
          }),
        );
        onActivity?.("Sunto aggiornato");
      } catch (e) {
        onActivity?.(e instanceof Error ? e.message : "Errore sunto");
      } finally {
        setBusy(false);
        setTimeout(() => onActivity?.(null), 2000);
      }
    },
    [deck, note, onActivity, onDeckChange, slideId],
  );

  const menuItems = useMemo(() => {
    const items = [
      {
        id: "pin",
        label: note.pinned ? "Rimuovi pin" : "Pin in cima",
        onClick: () => onDeckChange(toggleSpeakerNotePin(deck, slideId, note.id)),
      },
      {
        id: "edit",
        label: editingBody ? "Salva modifica" : "Modifica MD",
        disabled: busy,
        onClick: () => {
          if (editingBody) {
            commitBody();
          } else {
            setBodyDraft(note.body);
            setEditingBody(true);
            setCardMinimized(false);
          }
        },
      },
      {
        id: "sanitize",
        label: "Ripulisci",
        disabled: busy,
        onClick: handleSanitize,
      },
      {
        id: "regen",
        label: "Rigenera sunto…",
        disabled: busy,
        onClick: () => {
          setSummaryTargetOpen((v) => !v);
          setCardMinimized(false);
        },
      },
    ];

    if (hasRawBody) {
      items.push({
        id: "expand-body",
        label: isBodyExpanded ? "Comprimi risposta" : "Mostra risposta integrale",
        disabled: busy,
        onClick: handleToggleBodyExpand,
      });
    }

    items.push({
      id: "raw",
      label: "RAW",
      disabled: busy,
      onClick: () => setRawOpen(true),
    });

    for (const k of SPEAKER_NOTE_KINDS) {
      if (k === note.kind) continue;
      items.push({
        id: `kind-${k}`,
        label: `Tipo: ${KIND_LABELS[k]}`,
        onClick: () => onDeckChange(setSpeakerNoteKind(deck, slideId, note.id, k)),
      });
    }

    items.push({
      id: "remove",
      label: "Rimuovi",
      onClick: () => onDeckChange(removeSpeakerNote(deck, slideId, note.id)),
    });

    return items;
  }, [
    busy,
    deck,
    editingBody,
    handleRegenerateSummary,
    handleSanitize,
    handleToggleBodyExpand,
    hasRawBody,
    isBodyExpanded,
    note.body,
    note.id,
    note.kind,
    note.pinned,
    onDeckChange,
    slideId,
  ]);

  if (readOnly) {
    return (
      <article
        className={`surf-note-card surf-note-card--${note.kind} surf-note-card--read-only${
          note.pinned ? " is-pinned" : ""
        }${cardMinimized ? " is-minimized" : ""}${isBodyExpanded ? " is-body-expanded" : ""}`}
      >
        <div className="surf-note-card-accent" aria-hidden />

        <div className="surf-note-card-top">
          {question ? (
            <div className="surf-note-card-question">
              <span className="surf-note-card-question-label">Domanda</span>
              <p className="surf-note-card-question-text">{question}</p>
            </div>
          ) : (
            <div className="surf-note-card-question surf-note-card-question--empty">
              <span className="surf-note-card-question-label">Nota</span>
              <p className="surf-note-card-question-text">{note.title}</p>
            </div>
          )}
          <div className="surf-note-card-toolbar">
            <button
              type="button"
              className="surf-note-card-icon-btn"
              title={cardMinimized ? "Espandi nota" : "Comprimi nota"}
              aria-expanded={!cardMinimized}
              onClick={() => setCardMinimized((v) => !v)}
            >
              {cardMinimized ? "▸" : "▾"}
            </button>
          </div>
        </div>

        {!cardMinimized && (
          <>
            <header className="surf-note-card-head">
              <span className="surf-note-card-title surf-note-card-title--static">{note.title}</span>
              <span className={`surf-note-card-kind surf-note-card-kind--${note.kind}`}>
                {KIND_LABELS[note.kind]}
              </span>
              {note.summaryStatus === "fallback" && (
                <span
                  className="surf-note-card-summary-badge"
                  title="Sunto da troncamento, non ancora AI"
                >
                  Sunto rapido
                </span>
              )}
            </header>
            {hasRawBody && (
              <button
                type="button"
                className="surf-btn surf-btn--ghost surf-note-card-expand-btn"
                onClick={() => setBodyExpandedLocal((v) => !v)}
              >
                {isBodyExpanded ? "Comprimi risposta" : "Mostra risposta integrale"}
              </button>
            )}
            <div className="surf-note-card-body">
              <SpeakerScriptView markdown={displayMarkdown} className="surf-note-card-md-view" />
            </div>
          </>
        )}
      </article>
    );
  }

  return (
    <article
      className={`surf-note-card surf-note-card--${note.kind}${note.pinned ? " is-pinned" : ""}${
        dragOver ? " is-drag-over" : ""
      }${cardMinimized ? " is-minimized" : ""}${isBodyExpanded ? " is-body-expanded" : ""}`}
      draggable={!editingBody && !cardMinimized}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", String(index));
        e.dataTransfer.effectAllowed = "move";
        onDragStart(index);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(index);
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(index);
      }}
    >
      <div className="surf-note-card-accent" aria-hidden />

      <div className="surf-note-card-top">
        {question ? (
          <div className="surf-note-card-question">
            <span className="surf-note-card-question-label">Domanda</span>
            <p className="surf-note-card-question-text">{question}</p>
          </div>
        ) : (
          <div className="surf-note-card-question surf-note-card-question--empty">
            <span className="surf-note-card-question-label">Nota</span>
            <p className="surf-note-card-question-text">{note.title}</p>
          </div>
        )}
        <div className="surf-note-card-toolbar">
          <button
            type="button"
            className="surf-note-card-icon-btn"
            title={cardMinimized ? "Espandi nota" : "Comprimi nota"}
            aria-expanded={!cardMinimized}
            onClick={() => setCardMinimized((v) => !v)}
          >
            {cardMinimized ? "▸" : "▾"}
          </button>
          <Menu
            align="end"
            disabled={busy}
            label="⋯"
            items={menuItems}
          />
        </div>
      </div>

      {!cardMinimized && (
        <>
          <header className="surf-note-card-head">
            {editingTitle ? (
              <input
                className="surf-note-card-title-input"
                value={titleDraft}
                autoFocus
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitTitle();
                  if (e.key === "Escape") {
                    setTitleDraft(note.title);
                    setEditingTitle(false);
                  }
                }}
              />
            ) : (
              <button
                type="button"
                className="surf-note-card-title"
                onClick={() => {
                  setTitleDraft(note.title);
                  setEditingTitle(true);
                }}
              >
                {note.title}
              </button>
            )}
            <span className={`surf-note-card-kind surf-note-card-kind--${note.kind}`}>
              {KIND_LABELS[note.kind]}
            </span>
            {note.summaryStatus === "fallback" && (
              <span
                className="surf-note-card-summary-badge"
                title="Sunto da troncamento, non ancora AI"
              >
                Sunto rapido
              </span>
            )}
          </header>
          {summaryTargetOpen && (
            <div className="surf-note-card-summary-target">
              <label className="surf-note-card-summary-target-label" htmlFor={`summary-target-${note.id}`}>
                Lunghezza sunto: {summaryTarget} caratteri
              </label>
              <input
                id={`summary-target-${note.id}`}
                type="range"
                min={SPEAKER_NOTE_SUMMARY_CHARS_MIN}
                max={SPEAKER_NOTE_SUMMARY_CHARS_MAX}
                step={50}
                value={summaryTarget}
                disabled={busy}
                onChange={(e) => setSummaryTarget(Number(e.target.value))}
              />
              <button
                type="button"
                className="surf-btn surf-btn--ghost surf-note-card-summary-target-btn"
                disabled={busy}
                onClick={() => void handleRegenerateSummary(summaryTarget)}
              >
                Rigenera
              </button>
            </div>
          )}
          <div className="surf-note-card-body">
            {editingBody ? (
              <textarea
                className="surf-note-card-body-editor"
                value={bodyDraft}
                rows={6}
                onChange={(e) => setBodyDraft(e.target.value)}
                onBlur={commitBody}
              />
            ) : (
              <SpeakerScriptView markdown={displayMarkdown} className="surf-note-card-md-view" />
            )}
          </div>
        </>
      )}

      <SpeakerNoteRawModal
        title={note.title}
        rawMarkdown={note.rawBody ?? note.body}
        open={rawOpen}
        onClose={() => setRawOpen(false)}
      />
    </article>
  );
}
